import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  UserPlus,
  ArrowRight,
  CheckCircle2,
  ClipboardCopy,
  User as UserIcon,
  Phone,
  MapPin,
  Hash,
  Stethoscope,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";

export default function PatientRegister() {
  const { branch } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    age: "",
    gender: "Male",
    phone: "",
    address: "",
    complaint: "",
    assigned_doctor: "",
  });
  const [doctors, setDoctors] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!branch?.code) return;
    api
      .get("/doctors", { params: { branch_code: branch.code } })
      .then((r) => setDoctors(r.data))
      .catch(() => setDoctors([]));
  }, [branch?.code]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.age || !form.address.trim()) {
      toast.error("Fill name, age, phone and address");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        name: form.name,
        age: Number(form.age),
        gender: form.gender,
        phone: form.phone,
        address: form.address,
        complaint: form.complaint,
        branch_code: branch.code,
      };
      if (form.assigned_doctor) payload.assigned_doctor = form.assigned_doctor;
      const { data } = await api.post("/patients", payload);
      setResult(data);
      toast.success(`Registered · Token ${data.op_token}`);
    } catch (err) {
      toast.error(formatApiError(err, "Registration failed"));
    } finally {
      setBusy(false);
    }
  };

  const allocateNursing = async () => {
    if (!result) return;
    try {
      const { data } = await api.post(`/patients/${result.id}/allocate-nursing`);
      setResult(data);
      toast.success("Sent to nursing queue");
    } catch (err) {
      toast.error(formatApiError(err, "Could not allocate"));
    }
  };

  const registerAnother = () => {
    setResult(null);
    setForm({ name: "", age: "", gender: "Male", phone: "", address: "", complaint: "", assigned_doctor: "" });
  };

  const copyToken = () => {
    navigator.clipboard?.writeText(result.op_token).then(() => toast.success("Token copied"));
  };

  if (result) {
    return (
      <div className="max-w-3xl mx-auto animate-fade-up" data-testid="registration-success">
        <div className="mb-6 text-xs uppercase tracking-[0.28em] text-[#5C5652] ornament-dot">
          Registered · {branch.name}
        </div>
        <div className="bg-white border border-sand rounded-lg p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-11 w-11 rounded-lg bg-[#1A4331]/10 text-forest flex items-center justify-center">
              <CheckCircle2 className="h-6 w-6" strokeWidth={1.5} />
            </div>
            <div>
              <div className="font-display text-2xl text-forest">Patient registered</div>
              <div className="text-sm text-[#5C5652]">Details recorded and token issued.</div>
            </div>
          </div>

          <div className="grid gap-6 sm:grid-cols-2 mb-8">
            <div className="p-5 rounded-lg bg-[#F9F6F0] border border-sand">
              <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#5C5652] mb-2">
                OP Token (today)
              </div>
              <div className="flex items-center gap-3">
                <div className="font-display text-5xl text-forest leading-none" data-testid="result-token">
                  {result.op_token}
                </div>
                <button
                  onClick={copyToken}
                  className="h-9 w-9 rounded-lg border border-sand bg-white hover:bg-[#F9F6F0] flex items-center justify-center transition-colors"
                  data-testid="copy-token-button"
                  aria-label="Copy token"
                >
                  <ClipboardCopy className="h-4 w-4 text-[#5C5652]" strokeWidth={1.5} />
                </button>
              </div>
              <div className="mt-3 text-xs text-[#5C5652]">Branch prefix · daily sequential</div>
            </div>
            <div className="p-5 rounded-lg bg-[#F9F6F0] border border-sand">
              <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#5C5652] mb-2">
                Permanent MRN
              </div>
              <div className="font-display text-3xl text-forest" data-testid="result-mrn">{result.mrn}</div>
              <div className="mt-3 text-xs text-[#5C5652]">Medical Record Number · life-time</div>
            </div>
          </div>

          <div className="space-y-1 mb-8 text-sm">
            <Row label="Name" value={result.name} />
            <Row label="Age / Gender" value={`${result.age} · ${result.gender}`} />
            <Row label="Phone" value={result.phone} />
            <Row label="Address" value={result.address} />
            {result.complaint && <Row label="Complaint" value={result.complaint} />}
            {result.assigned_doctor_name && (
              <Row label="Consulting doctor" value={result.assigned_doctor_name} />
            )}
            <Row label="Status" value={result.status.replace("_", " ")} />
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              onClick={allocateNursing}
              disabled={result.status !== "registered"}
              data-testid="allocate-nursing-button"
              className="h-11 px-5 rounded-lg bg-forest text-white font-semibold hover:bg-forest-hover transition-colors disabled:opacity-60 inline-flex items-center gap-2"
            >
              {result.status === "in_nursing" ? "Sent to nursing" : "Send to nursing queue"}
              <ArrowRight className="h-4 w-4" strokeWidth={1.75} />
            </button>
            <button
              onClick={registerAnother}
              data-testid="register-another-button"
              className="h-11 px-5 rounded-lg border border-sand bg-white text-[#2A2624] font-semibold hover:bg-[#F9F6F0] transition-colors inline-flex items-center gap-2"
            >
              <UserPlus className="h-4 w-4" strokeWidth={1.75} />
              Register another
            </button>
            <button
              onClick={() => navigate("/patients")}
              className="h-11 px-5 rounded-lg text-forest font-semibold hover:bg-[#1A4331]/8 transition-colors"
              data-testid="go-to-list-button"
            >
              Go to patient list
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto animate-fade-up" data-testid="patient-register-page">
      <div className="text-xs uppercase tracking-[0.28em] text-[#5C5652] mb-3 ornament-dot">
        {branch.name} · new registration
      </div>
      <h1 className="font-display text-3xl sm:text-4xl text-forest mb-2">Register a patient</h1>
      <p className="text-[#5C5652] mb-8 max-w-xl">
        Capture the essentials — a permanent MRN and today&apos;s OP token will be issued on submit.
      </p>

      <form onSubmit={submit} className="bg-white border border-sand rounded-lg p-6 sm:p-8 grid gap-5" data-testid="patient-register-form">
        <Field label="Full name" icon={UserIcon}>
          <input
            data-testid="reg-name-input"
            value={form.name}
            onChange={update("name")}
            placeholder="e.g., Anitha Menon"
            className="w-full h-11 pl-10 pr-3 rounded-lg bg-white border border-sand focus:outline-none focus:ring-2 focus:ring-[#1A4331]/40 focus:border-[#1A4331] transition-colors"
          />
        </Field>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <Field label="Age" icon={Hash}>
            <input
              data-testid="reg-age-input"
              type="number"
              min="0"
              max="130"
              value={form.age}
              onChange={update("age")}
              placeholder="e.g., 42"
              className="w-full h-11 pl-10 pr-3 rounded-lg bg-white border border-sand focus:outline-none focus:ring-2 focus:ring-[#1A4331]/40 focus:border-[#1A4331] transition-colors"
            />
          </Field>
          <Field label="Gender">
            <div className="flex gap-2" data-testid="reg-gender-group">
              {["Male", "Female", "Other"].map((g) => (
                <button
                  type="button"
                  key={g}
                  data-testid={`reg-gender-${g.toLowerCase()}`}
                  onClick={() => setForm((f) => ({ ...f, gender: g }))}
                  className={`flex-1 h-11 rounded-lg border transition-colors ${
                    form.gender === g
                      ? "bg-forest text-white border-forest"
                      : "bg-white border-sand text-[#2A2624] hover:bg-[#F9F6F0]"
                  }`}
                >
                  {g}
                </button>
              ))}
            </div>
          </Field>
        </div>

        <Field label="Phone" icon={Phone}>
          <input
            data-testid="reg-phone-input"
            value={form.phone}
            onChange={update("phone")}
            placeholder="10-digit mobile number"
            inputMode="tel"
            className="w-full h-11 pl-10 pr-3 rounded-lg bg-white border border-sand focus:outline-none focus:ring-2 focus:ring-[#1A4331]/40 focus:border-[#1A4331] transition-colors"
          />
        </Field>

        <Field label="Address" icon={MapPin}>
          <textarea
            data-testid="reg-address-input"
            value={form.address}
            onChange={update("address")}
            placeholder="House, street, town, district"
            rows={2}
            className="w-full pl-10 pr-3 py-3 rounded-lg bg-white border border-sand focus:outline-none focus:ring-2 focus:ring-[#1A4331]/40 focus:border-[#1A4331] transition-colors resize-none"
          />
        </Field>

        <Field label="Chief complaint (optional)">
          <textarea
            data-testid="reg-complaint-input"
            value={form.complaint}
            onChange={update("complaint")}
            placeholder="Brief presenting complaint"
            rows={2}
            className="w-full px-3 py-3 rounded-lg bg-white border border-sand focus:outline-none focus:ring-2 focus:ring-[#1A4331]/40 focus:border-[#1A4331] transition-colors resize-none"
          />
        </Field>

        {/* Doctor allocation */}
        <div>
          <label className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#5C5652] mb-2">
            <Stethoscope className="h-3.5 w-3.5" strokeWidth={1.75} />
            Consulting doctor · optional
          </label>
          {doctors.length === 0 ? (
            <div className="p-4 rounded-lg bg-[#C25946]/8 border border-[#C25946]/30 text-sm text-[#8F3C2C]">
              No doctors assigned to this branch. Superadmin can map doctors from&nbsp;
              <span className="font-semibold">Doctor Mapping</span>.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="reg-doctor-picker">
              <button
                type="button"
                data-testid="reg-doctor-none"
                onClick={() => setForm((f) => ({ ...f, assigned_doctor: "" }))}
                className={`text-left p-3 rounded-lg border transition-colors ${
                  form.assigned_doctor === ""
                    ? "border-forest bg-[#1A4331]/6 text-forest"
                    : "border-sand hover:bg-[#F9F6F0] text-[#2A2624]"
                }`}
              >
                <div className="font-semibold">Decide later</div>
                <div className="text-[10px] uppercase tracking-wider text-[#5C5652]">
                  Nurse will pick the doctor after vitals
                </div>
              </button>
              {doctors.map((d) => (
                <button
                  type="button"
                  key={d.username}
                  data-testid={`reg-doctor-${d.username}`}
                  onClick={() => setForm((f) => ({ ...f, assigned_doctor: d.username }))}
                  className={`text-left p-3 rounded-lg border-2 transition-all ${
                    form.assigned_doctor === d.username
                      ? "border-forest bg-forest text-white ring-2 ring-forest ring-offset-2 shadow-md"
                      : "border-sand bg-white hover:bg-[#F9F6F0] text-[#2A2624]"
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-semibold">{d.name}</div>
                      <div className={`text-[10px] uppercase tracking-wider ${form.assigned_doctor === d.username ? "text-white/80" : "text-[#5C5652]"}`}>
                        {d.username}
                      </div>
                    </div>
                    {form.assigned_doctor === d.username && <span className="text-white text-lg leading-none">✓</span>}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="p-4 rounded-lg bg-[#F9F6F0] border border-sand flex items-center justify-between text-sm">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#5C5652]">
              Branch
            </div>
            <div className="font-semibold text-forest">{branch?.name} · {branch?.code}</div>
          </div>
          <div className="text-xs text-[#5C5652]">Token will use prefix <span className="font-semibold text-forest">{branch?.code}-###</span></div>
        </div>

        <button
          type="submit"
          disabled={busy}
          data-testid="reg-submit-button"
          className="h-11 rounded-lg bg-forest text-white font-semibold hover:bg-forest-hover transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
        >
          {busy ? "Registering…" : "Register patient"}
          {!busy && <ArrowRight className="h-4 w-4" strokeWidth={1.75} />}
        </button>
      </form>
    </div>
  );
}

function Field({ label, icon: Icon, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-wider text-[#5C5652] mb-2">
        {label}
      </label>
      <div className="relative">
        {Icon && (
          <Icon
            className="h-4 w-4 absolute left-3 top-3.5 text-[#5C5652]"
            strokeWidth={1.5}
          />
        )}
        {children}
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-baseline gap-4 py-1.5 border-b border-dashed border-sand last:border-0">
      <div className="w-32 text-[10px] font-bold uppercase tracking-[0.18em] text-[#5C5652] flex-shrink-0">
        {label}
      </div>
      <div className="text-[#2A2624]">{value}</div>
    </div>
  );
}
